/**
 * Currency Conversion Options Selection
 * - allows the user to select between converting with PayPal or through their card company
 * - used to select a currency conversion option in the overpanel
 * - assumes this is rendered in the <Overpanel/>
 */

import PropTypes from 'prop-types'
import React from 'react'
import glamorous from 'glamorous'
import {
  extraSmallAndSmaller,
  smallAndSmaller,
} from '../../lib/styles/media-queries'
import { CURRENCY_CONVERSION_OPTIONS } from '../constants'
import { trackLinkWithData } from '../../lib/analytics/fpti'
import content from 'pp-react-l10n'
import { getDisclaimerMessage } from '../utils/currencyConversionFeeDisclaimer-utils'
import { isBFXCurrencyConversionTrmt } from '../../experiments/bfx-currency-conversion'
import { BFXCurrencyConversion } from './bfx-currency-conversion'
let i18n = content('transfer/inc/currencyConversion')

const Title = glamorous.h2({
  margin: '0 0 20px 0px',
  textAlign: 'center',
  [extraSmallAndSmaller]: {
    margin: '0 0 20px 0',
    fontSize: 'x-large',
  },
})

const HeaderInfo = glamorous.p({
  margin: '0 auto 10px',
  maxWidth: '350px',
  fontSize: '14px',
})

const BlockInfo = glamorous.div({
  maxWidth: '350px',
  fontSize: '14px',
})

const RadioOptions = glamorous.div({
  margin: '0 auto 0 50px',
  [extraSmallAndSmaller]: {
    margin: 'unset',
  },
})

const CtaButton = glamorous.input({
  display: 'block',
  margin: '30px auto 0',
  minWidth: '280px',
  [smallAndSmaller]: {
    minWidth: '168px',
  },
})

/**
 * The header for the currency conversion selection overpanel
 */
const ConversionHeader = ({
  showAdditionalInfo,
  convertWithPayPal,
  rate,
  chooseOptionHeader,
  paypalExchangeRateInfo,
  creditCardExchangeRateInfo,
  feeInfo,
}) => {
  // when showing all the additional info for each option, show the simpler header info
  if (showAdditionalInfo) {
    return (
      <div>
        <HeaderInfo>{chooseOptionHeader}</HeaderInfo>
        {convertWithPayPal && <HeaderInfo>{feeInfo}</HeaderInfo>}
      </div>
    )
  }

  return (
    <div style={{ marginBottom: 30, textAlign: 'center' }}>
      {convertWithPayPal && rate && (
        <HeaderInfo css={{ fontWeight: '700' }}>
          {paypalExchangeRateInfo}
        </HeaderInfo>
      )}

      {!convertWithPayPal && (
        <HeaderInfo>{creditCardExchangeRateInfo}</HeaderInfo>
      )}

      {convertWithPayPal && <HeaderInfo>{feeInfo}</HeaderInfo>}
    </div>
  )
}
ConversionHeader.propTypes = {
  showAdditionalInfo: PropTypes.bool.isRequired,
  convertWithPayPal: PropTypes.bool.isRequired,
  rate: PropTypes.string,
  chooseOptionHeader: PropTypes.string.isRequired,
  paypalExchangeRateInfo: PropTypes.array.isRequired,
  creditCardExchangeRateInfo: PropTypes.string.isRequired,
  feeInfo: PropTypes.string.isRequired,
}

/**
 * Conversion rate from PayPal with additional info
 */
const ConversionRateAndInfo = ({
  showAdditionalInfo,
  conversionRate,
  info,
}) => {
  if (!showAdditionalInfo) {
    return null
  }

  return (
    <div>
      <BlockInfo>{conversionRate}</BlockInfo>
      <BlockInfo>{info}</BlockInfo>
    </div>
  )
}
ConversionRateAndInfo.propTypes = {
  showAdditionalInfo: PropTypes.bool.isRequired,
  conversionRate: PropTypes.array.isRequired,
  info: PropTypes.string.isRequired,
}

/**
 * The convert with paypal radio option
 * We need to show extra info for BR
 */
const ConvertWithPayPal = ({
  checked,
  handleChange,
  label,
  showAdditionalInfo,
  conversionRate,
  info,
}) => {
  return (
    <div className="vx_radio" style={{ marginBottom: 8 }}>
      <input
        type="radio"
        name="choice"
        id="internalConversion"
        data-nemo="internal-conversion"
        value={CURRENCY_CONVERSION_OPTIONS.INTERNAL}
        className="accessAid"
        checked={checked}
        onChange={handleChange}
      />
      <label htmlFor="internalConversion" data-nemo="internal-conversion-label">
        {label}
      </label>

      <ConversionRateAndInfo
        showAdditionalInfo={showAdditionalInfo}
        conversionRate={conversionRate}
        info={info}
      />
    </div>
  )
}
ConvertWithPayPal.propTypes = {
  checked: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  showAdditionalInfo: PropTypes.bool.isRequired,
  conversionRate: PropTypes.array.isRequired,
  info: PropTypes.string.isRequired,
}

/**
 * The convert with card issuer radio option
 * We need to show extra info for BR
 */
const ConvertWithCard = ({
  checked,
  handleChange,
  label,
  showAdditionalInfo,
  info,
}) => {
  return (
    <div className="vx_radio">
      <input
        type="radio"
        name="choice"
        id="externalConversion"
        data-nemo="external-conversion"
        value={CURRENCY_CONVERSION_OPTIONS.EXTERNAL}
        className="accessAid"
        checked={checked}
        onChange={handleChange}
      />
      <label htmlFor="externalConversion" data-nemo="external-conversion-label">
        {label}
      </label>

      {showAdditionalInfo && <BlockInfo>{info}</BlockInfo>}
    </div>
  )
}
ConvertWithCard.propTypes = {
  checked: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  showAdditionalInfo: PropTypes.bool.isRequired,
  info: PropTypes.string.isRequired,
}

// This is a base, non-`connect`ed component that can be consumed by flow-specific
// parent, `connect`ed components. TODO: refactor the guest use of the above component
// to use this new paradigm
export class CurrencyConversionSelectionBase extends React.Component {
  static propTypes = {
    showAdditionalConversionInfo: PropTypes.bool.isRequired,
    currencyCode: PropTypes.string.isRequired,
    currencyDecimal: PropTypes.string.isRequired,
    selectedFundingOption: PropTypes.object.isRequired,
    closeCurrencyConversionOverpanel: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    userCountry: PropTypes.string,
    ppCurrencyExchange: PropTypes.object,
    isFeeDisclaimerMessageDisabled: PropTypes.bool,
  }

  constructor(props) {
    super(props)
    const { currency_exchange } = props.selectedFundingOption

    this.state = {
      selectedOption: currency_exchange ? 'INTERNAL' : 'EXTERNAL',
    }
  }

  handleChange = event => {
    this.setState({ selectedOption: event.target.value })
  }

  handleSubmit = event => {
    event.preventDefault()
    event.stopPropagation()

    const { currency_exchange } = this.props.selectedFundingOption
    const currentCurrencyConversionOption = currency_exchange
      ? 'INTERNAL'
      : 'EXTERNAL'

    // track default and selection
    const pageGroup = 'main:consumer:p2p:transfer:currencyConversionOption'
    const pageName =
      'main:consumer:p2p:transfer:currencyConversionOption:::node'

    const extraData = {
      opsel: this.state.selectedOption,
      dflopt: currentCurrencyConversionOption,
    }
    trackLinkWithData('next', pageGroup, pageName, extraData, 'link')

    // if user has not selected different option, close overpanel and return out
    if (currentCurrencyConversionOption === this.state.selectedOption) {
      return this.props.closeCurrencyConversionOverpanel()
    }

    this.props.handleSubmit(this.state.selectedOption)
  }

  render() {
    let convertWithPayPal =
      this.state.selectedOption === CURRENCY_CONVERSION_OPTIONS.INTERNAL
    const userCountry = this.props.userCountry
    const isFeeDisclaimerMessageDisabled = this.props
      .isFeeDisclaimerMessageDisabled
    // hard code the value to be true as we don't need to display any fee related content here
    const isPurchase = true
    const feeDisclaimerMessage = getDisclaimerMessage({
      convertWithPayPal,
      userCountry,
      convertUsingCard: false,
      isPurchase,
      isFeeDisclaimerMessageDisabled,
    })
    const creditCardExchangeRateInfo = getDisclaimerMessage({
      convertWithPayPal,
      userCountry,
      convertUsingCard: true,
      isPurchase,
    })
    let showAdditionalInfo = this.props.showAdditionalConversionInfo
    let currency_exchange = this.props.ppCurrencyExchange || {}

    let {
      exchange_funds_in: { currency_code: fundingOptionCurrencyCode = '' } = {},
      exchange_rate = '',
    } = currency_exchange

    const rate = exchange_rate
      .toString()
      .replace('.', this.props.currencyDecimal)

    let paypalExchangeRateData = {
      fundingOptionCurrencyCode: fundingOptionCurrencyCode,
      rate,
      currencyCode: this.props.currencyCode,
    }

    // if in new currency conversion treatment, render new conversion page here
    if (isBFXCurrencyConversionTrmt()) {
      return (
        <BFXCurrencyConversion
          ppCurrencyExchange={this.props.ppCurrencyExchange}
          currencyDecimal={this.props.currencyDecimal}
          selectedFundingOption={this.props.selectedFundingOption}
          handleSubmit={this.props.handleSubmit}
          closeCurrencyConversionOverpanel={
            this.props.closeCurrencyConversionOverpanel
          }
        />
      )
    }

    return (
      <div>
        <div id="overpanel-header" className="overpanel-header">
          <Title>{i18n('options.currencyConversion')}</Title>

          <ConversionHeader
            showAdditionalInfo={showAdditionalInfo}
            convertWithPayPal={convertWithPayPal}
            rate={exchange_rate}
            chooseOptionHeader={i18n('header.chooseOption')}
            selectCurrencyOptionsCopy={i18n('options.selectCurrencyOptions')}
            paypalExchangeRateInfo={i18n.html(
              'options.paypalExchangeRate',
              paypalExchangeRateData
            )}
            creditCardExchangeRateInfo={creditCardExchangeRateInfo}
            feeInfo={feeDisclaimerMessage}
          />

          <form
            name="currencyConversionPreference"
            method="post"
            onSubmit={this.handleSubmit}
          >
            <RadioOptions>
              <ConvertWithPayPal
                checked={convertWithPayPal}
                handleChange={this.handleChange}
                label={
                  showAdditionalInfo
                    ? i18n('label.ppRate')
                    : i18n('options.convertWithPaypal')
                }
                showAdditionalInfo={showAdditionalInfo}
                conversionRate={i18n('conversionRate', paypalExchangeRateData, {
                  useHTML: true,
                })}
                info={i18n.html('info.ppRate')}
              />

              <ConvertWithCard
                checked={!convertWithPayPal}
                handleChange={this.handleChange}
                label={
                  showAdditionalInfo
                    ? i18n('label.cardIssuerRate')
                    : i18n('options.convertWithCreditCard')
                }
                showAdditionalInfo={showAdditionalInfo}
                info={i18n.html('info.cardIssuerRate')}
              />
            </RadioOptions>
            <CtaButton
              type="submit"
              id="currencyConversionNext"
              data-nemo="currency-conversion-next"
              className="vx_btn"
              value={i18n('options.continue')}
              style={{ marginTop: 30 }}
            />
          </form>
        </div>
      </div>
    )
  }
}
